import type { ActionName } from 'state/common-form/infos/infos.model';
import { getQuoteActivities } from 'state/quotes/selectors';
import {
  getFormQuoteResult,
  getQuotePriceInPercent,
  getQuoteRemunerationMode,
  type WorkFlowStatus,
} from 'state/selectors';
import type { AppState } from 'state/state.model';
import * as fromAdvisory from 'state/uiPanelState/advisory/advisory.selectors';
import * as fromQuoteActionsUi from 'state/uiPanelState/quoteActions/quoteActions.selectors';
import type { SidePanelTabId } from 'state/uiPanelState/sidePanelUi/sidePanelUi.model';
import * as fromSidePanel from 'state/uiPanelState/sidePanelUi/sidePanelUi.selectors';
import { WorkspaceType } from 'state/workspaces/model';

export function isBasketOptimizerAccordionOpened(state: AppState, tabId: string): boolean {
  const advisoryUiState = state.uiPanel?.advisoryUi;
  return fromAdvisory.isBasketOptimizerAccordionOpened(advisoryUiState, tabId);
}

export function getActiveTabInSidePanel(state: AppState, tabId: string): SidePanelTabId {
  const sidePanelUiState = state.uiPanel?.sidePanelUi;
  return fromSidePanel.getActiveTabInSidePanel(sidePanelUiState, tabId);
}
export function getActiveQuoteActionFor(state: AppState, tabId: string, actionName: ActionName): ActionName {
  const quoteActionsUiState = state.uiPanel?.quoteActionsUi;
  return fromQuoteActionsUi.getActiveQuoteActionFor(quoteActionsUiState, tabId, actionName);
}
export function hasBasketOptimizer(state: AppState, quoteId: string): boolean {
  const activities = getQuoteActivities(state, quoteId);

  return activities?.isBasketOptimizerEnabled ?? false;
}

export function hasAdvisory(state: AppState, quoteId: string, quoteStatus: WorkFlowStatus): boolean {
  const hasOneAdvisoryCard = hasBasketOptimizer(state, quoteId);

  return (
    (quoteStatus === 'QUOTED' || quoteStatus === 'QUOTED_TRADABLE' || quoteStatus === 'QUOTED_PREVIOUSLY') &&
    hasOneAdvisoryCard
  );
}

export function getSidePanelMetadata(state: AppState, tabId: string) {
  const sidePanelUiState = state.uiPanel?.sidePanelUi;
  return fromSidePanel.getSidePanelMetadata(sidePanelUiState, tabId);
}

export function getSidePanelTradeWay(state: AppState, tabId: string) {
  return getSidePanelMetadata(state, tabId)?.tradeWay;
}

export function getSidePanelAddonAction(state: AppState, tabId: string, workspaceType: WorkspaceType) {
  const isExecOnlyAddon = getFormQuoteResult(state, workspaceType, tabId)?.isExecOnlyAddon;
  return isExecOnlyAddon ? getSidePanelMetadata(state, tabId)?.addonAction : undefined;
}

export function getSidePanelAddonActionOrDefault(state: AppState, tabId: string, workspaceType: WorkspaceType) {
  return getSidePanelAddonAction(state, tabId, workspaceType) ?? 'Increase';
}

export function getSidePanelTradeWayOrDefault(state: AppState, tabId: string) {
  return getSidePanelMetadata(state, tabId)?.tradeWay ?? 'Sell';
}
export function getFlowWay(state: AppState, tabId: string) {
  const priceInPercent = getQuotePriceInPercent(state, tabId);
  const remunerationMode = getQuoteRemunerationMode(state, tabId);
  if (priceInPercent > 0 && remunerationMode === 'Reoffer') {
    return 'YouPaySgReceives';
  }

  if (priceInPercent > 0 && remunerationMode === 'Upfront') {
    return 'YouReceiveSgPaysYou';
  }

  if (priceInPercent > 0 && remunerationMode === 'Premium') {
    return 'YouReceiveSgPaysYou';
  }

  if (priceInPercent < 0 && remunerationMode === 'Reoffer') {
    return 'YouReceiveSgPaysYou';
  }

  if (priceInPercent < 0 && remunerationMode === 'Upfront') {
    return 'YouPaySgReceives';
  }

  if (priceInPercent < 0 && remunerationMode === 'Premium') {
    return 'YouPaySgReceives';
  }

  return 'NoFlowWay';
}
